import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Command, ArrowRight } from 'lucide-react';

const CommandPalette = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Command Registry - use useMemo to avoid recreating on every render
  const commands = React.useMemo(() => [
    // Navigation Commands
    {
      id: 'dashboard',
      keywords: ['dashboard', 'home', 'main'],
      action: () => navigate('/home'),
      description: 'Open Dashboard',
      category: 'Navigation'
    },
    {
      id: 'emirates',
      keywords: ['emirates', 'emirate'],
      action: () => navigate('/home/masters/emirates'),
      description: 'Open Emirates page',
      category: 'Navigation'
    },
    {
      id: 'areas',
      keywords: ['areas', 'area'],
      action: () => navigate('/home/masters/areas'),
      description: 'Open Areas page',
      category: 'Navigation'
    },
    {
      id: 'locations',
      keywords: ['locations', 'location'],
      action: () => navigate('/home/masters/locations'),
      description: 'Open Locations page',
      category: 'Navigation'
    },
    {
      id: 'expenses',
      keywords: ['expenses', 'expense'],
      action: () => navigate('/home/masters/expenses'),
      description: 'Open Expenses page',
      category: 'Navigation'
    },
    {
      id: 'reasons',
      keywords: ['reasons', 'reason'],
      action: () => navigate('/home/masters/reasons'),
      description: 'Open Reasons page',
      category: 'Navigation'
    },
    {
      id: 'types',
      keywords: ['types', 'type'],
      action: () => navigate('/home/masters/types'),
      description: 'Open Types page',
      category: 'Navigation'
    },
    {
      id: 'vehicles',
      keywords: ['vehicles', 'vehicle'],
      action: () => navigate('/home/masters/vehicles'),
      description: 'Open Vehicles page',
      category: 'Navigation'
    },
    {
      id: 'designations',
      keywords: ['designations', 'designation'],
      action: () => navigate('/home/masters/designations'),
      description: 'Open Designations page',
      category: 'Navigation'
    },
    {
      id: 'employees',
      keywords: ['employees', 'employee list', 'staff', 'view employees'],
      action: () => navigate('/home/accounts/employees'),
      description: 'Open Employees page',
      category: 'Navigation'
    },
    {
      id: 'clients',
      keywords: ['clients', 'client list', 'view clients'],
      action: () => navigate('/home/accounts/clients'),
      description: 'Open Clients page',
      category: 'Navigation'
    },
    {
      id: 'customers',
      keywords: ['customers', 'customer list', 'view customers'],
      action: () => navigate('/home/accounts/customers'),
      description: 'Open Customers page',
      category: 'Navigation'
    },
    {
      id: 'agents',
      keywords: ['agents', 'agent list', 'view agents'],
      action: () => navigate('/home/accounts/agents'),
      description: 'Open Agents page',
      category: 'Navigation'
    },
    {
      id: 'track-voucher',
      keywords: ['track voucher', 'voucher', 'track by voucher'],
      action: () => navigate('/home/tracking/voucher'),
      description: 'Track by Voucher',
      category: 'Tracking'
    },
    {
      id: 'track-bill',
      keywords: ['track bill', 'bill', 'track by bill'],
      action: () => navigate('/home/tracking/bill'),
      description: 'Track by Bill',
      category: 'Tracking'
    },
    {
      id: 'track-customer',
      keywords: ['track customer', 'customer tracking'],
      action: () => navigate('/home/tracking/customer'),
      description: 'Track by Customer',
      category: 'Tracking'
    },
    {
      id: 'driver-tracking',
      keywords: ['driver tracking', 'track driver', 'drivers'],
      action: () => navigate('/home/tracking/driver'),
      description: 'Driver Tracking',
      category: 'Tracking'
    },
    {
      id: 'vehicle-dashboard',
      keywords: ['vehicle dashboard', 'fleet dashboard', 'dashboards vehicle'],
      action: () => navigate('/home/dashboards/vehicle'),
      description: 'Vehicle dashboard',
      category: 'Home'
    },
    {
      id: 'vehicle-list',
      keywords: ['vehicle list', 'vehicles list', 'vehicle management'],
      action: () => navigate('/home/vehicle-management/vehicle-list'),
      description: 'Vehicle List',
      category: 'Vehicle Management'
    },
    {
      id: 'maintenance',
      keywords: ['maintenance', 'vehicle maintenance'],
      action: () => navigate('/home/vehicle-management/maintenance'),
      description: 'Vehicle Maintenance',
      category: 'Vehicle Management'
    },
    {
      id: 'assignments',
      keywords: ['assignments', 'vehicle assignments'],
      action: () => navigate('/home/vehicle-management/assignments'),
      description: 'Vehicle Assignments',
      category: 'Vehicle Management'
    },
    {
      id: 'documents',
      keywords: ['documents', 'vehicle documents'],
      action: () => navigate('/home/vehicle-management/documents'),
      description: 'Vehicle Documents',
      category: 'Vehicle Management'
    },
    {
      id: 'insurance',
      keywords: ['insurance', 'vehicle insurance'],
      action: () => navigate('/home/vehicle-management/insurance'),
      description: 'Vehicle Insurance',
      category: 'Vehicle Management'
    },
    {
      id: 'fine-records',
      keywords: ['fine records', 'fines', 'vehicle fines'],
      action: () => navigate('/home/vehicle-management/fine-records'),
      description: 'Vehicle Fine Records',
      category: 'Vehicle Management'
    },
    {
      id: 'fuel-logs',
      keywords: ['fuel logs', 'vehicle fuel', 'fuel'],
      action: () => navigate('/home/vehicle-management/fuel-logs'),
      description: 'Vehicle Fuel Logs',
      category: 'Vehicle Management'
    },
    {
      id: 'vehicle-permits',
      keywords: ['vehicle permits', 'permits'],
      action: () => navigate('/home/vehicle-management/permits'),
      description: 'Vehicle Permits',
      category: 'Vehicle Management'
    },
    {
      id: 'vehicle-inspections',
      keywords: ['vehicle inspections', 'inspections'],
      action: () => navigate('/home/vehicle-management/inspections'),
      description: 'Vehicle Inspections',
      category: 'Vehicle Management'
    },
    {
      id: 'vehicle-expenses',
      keywords: ['vehicle expenses', 'fleet expenses'],
      action: () => navigate('/home/vehicle-management/vehicle-expenses'),
      description: 'Vehicle Expenses',
      category: 'Vehicle Management'
    },
    {
      id: 'odometer-logs',
      keywords: ['odometer', 'mileage logs'],
      action: () => navigate('/home/vehicle-management/odometer-logs'),
      description: 'Vehicle Odometer Logs',
      category: 'Vehicle Management'
    },
    {
      id: 'vehicle-accidents',
      keywords: ['vehicle accidents', 'accidents'],
      action: () => navigate('/home/vehicle-management/accidents'),
      description: 'Vehicle Accidents',
      category: 'Vehicle Management'
    },
    {
      id: 'service-history',
      keywords: ['service history', 'vehicle service'],
      action: () => navigate('/home/vehicle-management/service-history'),
      description: 'Vehicle Service History',
      category: 'Vehicle Management'
    },
    {
      id: 'draft-emails',
      keywords: ['draft emails', 'emails', 'messages'],
      action: () => navigate('/home/messages/draft-emails'),
      description: 'Draft Emails',
      category: 'Messages'
    },
    // Action Commands with Parameters
    {
      id: 'add-employee',
      keywords: ['add employee', 'new employee', 'create employee', 'employee named', 'add employee named'],
      action: (params) => {
        const name = params?.name || '';
        const url = `/home/accounts/addEmployee${name ? `?name=${encodeURIComponent(name)}` : ''}`;
        console.log('=== NAVIGATION TO ADD EMPLOYEE ===');
        console.log('URL:', url);
        console.log('Params:', params);
        console.log('Navigate function:', navigate);
        console.log('Calling navigate with:', url);
        try {
          navigate(url, { replace: false });
          console.log('Navigate called successfully to:', url);
        } catch (err) {
          console.error('Navigation error:', err);
        }
      },
      description: 'Add Employee',
      category: 'Actions',
      priority: 1, // Higher priority to match first
      parseCommand: (command) => {
        console.log('=== PARSING COMMAND ===');
        console.log('Input command:', command);
        
        // Try multiple patterns to extract name
        const patterns = [
          // Pattern 1: "add employee named Aji" or "add employee name Aji"
          /(?:add|new|create)\s+employee\s+(?:named|name)\s+([^\s]+(?:\s+[^\s]+)*)/i,
          // Pattern 2: "employee named Aji"
          /employee\s+(?:named|name)\s+([^\s]+(?:\s+[^\s]+)*)/i,
          // Pattern 3: "add employee Aji" (without "named" keyword) - captures everything after "employee" until end
          /(?:add|new|create)\s+employee\s+(.+?)$/i,
          // Pattern 4: "employee Aji" (just employee followed by name)
          /^employee\s+(.+?)$/i,
          // Pattern 5: Just "named" keyword
          /named\s+([^\s]+(?:\s+[^\s]+)*)/i,
          // Pattern 6: "with name"
          /with\s+name\s+([^\s]+(?:\s+[^\s]+)*)/i
        ];
        
        for (let i = 0; i < patterns.length; i++) {
          const pattern = patterns[i];
          const match = command.match(pattern);
          console.log(`Pattern ${i + 1} test:`, pattern, 'match:', match);
          
          if (match && match[1]) {
            let extractedName = match[1].trim();
            console.log('Extracted name (raw):', extractedName);
            
            // Remove any trailing keywords that might have been captured
            extractedName = extractedName.replace(/\s+(employee|named|name|with)\s*$/i, '').trim();
            console.log('Extracted name (cleaned):', extractedName);
            
            // Make sure we have a valid name and didn't match just keywords
            if (extractedName && 
                extractedName.length > 0 && 
                !extractedName.toLowerCase().includes('employee') &&
                extractedName.toLowerCase() !== 'named' &&
                extractedName.toLowerCase() !== 'name') {
              console.log('Returning name:', extractedName);
              return { name: extractedName };
            } else {
              console.log('Name validation failed:', extractedName);
            }
          }
        }
        
        console.log('No pattern matched, returning null');
        return null;
      }
    },
    {
      id: 'add-vehicle',
      keywords: ['add vehicle', 'new vehicle', 'create vehicle'],
      action: () => navigate('/home/vehicle-management/add-edit-vehicle'),
      description: 'Add Vehicle',
      category: 'Actions'
    },
    {
      id: 'add-maintenance',
      keywords: ['add maintenance', 'new maintenance', 'create maintenance'],
      action: () => navigate('/home/vehicle-management/add-maintenance'),
      description: 'Add Maintenance',
      category: 'Actions'
    },
    {
      id: 'add-document',
      keywords: ['add document', 'new document', 'upload document'],
      action: () => navigate('/home/vehicle-management/add-documents'),
      description: 'Add Document',
      category: 'Actions'
    },
    {
      id: 'add-insurance',
      keywords: ['add insurance', 'new insurance', 'create insurance'],
      action: () => navigate('/home/vehicle-management/add-insurance'),
      description: 'Add Insurance',
      category: 'Actions'
    },
    {
      id: 'add-assignment',
      keywords: ['add assignment', 'new assignment', 'assign driver'],
      action: () => navigate('/home/vehicle-management/add-assignment'),
      description: 'Add Assignment',
      category: 'Actions'
    },
    {
      id: 'add-fine',
      keywords: ['add fine', 'new fine', 'create fine'],
      action: () => navigate('/home/vehicle-management/add-fine'),
      description: 'Add Fine',
      category: 'Actions'
    },
    {
      id: 'add-client',
      keywords: ['add client', 'new client', 'create client'],
      action: () => navigate('/home/accounts/addClient'),
      description: 'Add Client',
      category: 'Actions'
    },
    {
      id: 'add-customer',
      keywords: ['add customer', 'new customer', 'create customer'],
      action: () => navigate('/home/accounts/addCustomer'),
      description: 'Add Customer',
      category: 'Actions'
    },
    {
      id: 'add-agent',
      keywords: ['add agent', 'new agent', 'create agent'],
      action: () => navigate('/home/accounts/addAgent'),
      description: 'Add Agent',
      category: 'Actions'
    }
  ], [navigate]);

  // Filter commands based on search term with improved matching
  const filteredCommands = React.useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) {
      return commands.slice(0, 10);
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(w => w.length > 0);
    
    const filtered = commands.filter(cmd => {
      // Special handling for commands with parameters (like "add employee Aji")
      // Check if search starts with command keywords
      const startsWithCommand = cmd.keywords.some(keyword => {
        const keywordLower = keyword.toLowerCase();
        // Check if search starts with keyword (e.g., "add employee" matches "add employee aji")
        if (searchLower.startsWith(keywordLower)) return true;
        // Also check if keyword is at the beginning of search (with space)
        const keywordWords = keywordLower.split(/\s+/);
        if (keywordWords.length > 0) {
          const firstKeywordWord = keywordWords[0];
          if (searchWords[0] === firstKeywordWord && searchWords.length >= keywordWords.length) {
            // Check if first few words match the keyword
            const searchPrefix = searchWords.slice(0, keywordWords.length).join(' ');
            if (searchPrefix === keywordLower) return true;
          }
        }
        return false;
      });
      
      // Check keyword matching - at least one keyword should match
      const keywordMatch = cmd.keywords.some(keyword => {
        const keywordLower = keyword.toLowerCase();
        // Exact match or contains all search words
        if (keywordLower === searchLower) return true;
        if (keywordLower.includes(searchLower)) return true;
        // Check if all search words are in keyword
        return searchWords.every(word => keywordLower.includes(word));
      });
      
      // Check description matching
      const descriptionMatch = cmd.description.toLowerCase().includes(searchLower) ||
                             searchWords.some(word => cmd.description.toLowerCase().includes(word));
      
      // For commands with parameters, check if search term contains command keywords
      const containsCommandPrefix = cmd.keywords.some(keyword => {
        const keywordLower = keyword.toLowerCase();
        return searchLower.includes(keywordLower);
      });
      
      return startsWithCommand || keywordMatch || descriptionMatch || containsCommandPrefix;
    });
    
    console.log('=== FILTERING COMMANDS ===');
    console.log('Search term:', searchTerm);
    console.log('Total commands:', commands.length);
    console.log('Filtered results:', filtered.map(c => ({ 
      id: c.id, 
      desc: c.description,
      keywords: c.keywords 
    })));
    
    // If search starts with "add employee", prioritize add-employee command
    const searchLowerTrim = searchLower.trim();
    if (searchLowerTrim.startsWith('add employee') || searchLowerTrim.startsWith('new employee') || searchLowerTrim.startsWith('create employee')) {
      console.log('Search starts with "add/new/create employee" - prioritizing add-employee command');
      const addEmployeeCmd = commands.find(c => c.id === 'add-employee');
      if (addEmployeeCmd) {
        // Check if it's already in filtered results
        const alreadyInResults = filtered.some(c => c.id === 'add-employee');
        if (!alreadyInResults) {
          console.log('Adding add-employee command to results (priority)');
          filtered.unshift(addEmployeeCmd); // Add to beginning
        } else {
          // Move it to the front
          const index = filtered.findIndex(c => c.id === 'add-employee');
          if (index > 0) {
            filtered.splice(index, 1);
            filtered.unshift(addEmployeeCmd);
          }
        }
      }
    }
    
    return filtered.slice(0, 10);
  }, [commands, searchTerm]);

  // Group commands by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) {
      acc[cmd.category] = [];
    }
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  // Enhanced command parsing for natural language
  const parseCommandParams = (command, searchText) => {
    if (!command.parseCommand) return null;
    
    try {
      return command.parseCommand(searchText);
    } catch (err) {
      console.error('Error parsing command:', err);
      return null;
    }
  };

  // Handle command execution
  const executeCommand = useCallback((command) => {
    if (!command) {
      console.error('No command provided to execute');
      return;
    }
    
    let params = null;
    
    // Try to parse parameters if parseCommand function exists
    if (command.parseCommand) {
      params = parseCommandParams(command, searchTerm);
      console.log('Command params parsed:', params, 'from:', searchTerm);
    }
    
    try {
      console.log('=== EXECUTING COMMAND ===');
      console.log('Command ID:', command.id);
      console.log('Command description:', command.description);
      console.log('Params:', params);
      console.log('Action type:', typeof command.action);
      console.log('Action function:', command.action);
      
      if (typeof command.action === 'function') {
        console.log('Calling action function with params:', params);
        // Execute the action
        const result = command.action(params);
        console.log('Action result:', result);
        console.log('Navigation should have happened');
        
        // Close the palette after navigation - give it more time
        setTimeout(() => {
          console.log('Closing palette now');
          onClose();
          setSearchTerm('');
        }, 200);
      } else {
        console.error('Command action is not a function:', command.action);
      }
    } catch (err) {
      console.error('Error executing command:', err);
      console.error('Error stack:', err.stack);
      // Don't close on error so user can see what happened
    }
  }, [searchTerm, onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      // Handle Enter key - check if we're in the input field
      if (e.key === 'Enter') {
        // Only prevent default if we have commands to execute
        if (filteredCommands.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          const commandToExecute = filteredCommands[selectedIndex] || filteredCommands[0];
          if (commandToExecute) {
            console.log('Executing command from Enter key:', commandToExecute.id);
            executeCommand(commandToExecute);
          }
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, selectedIndex, filteredCommands, searchTerm, executeCommand]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-20">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center border-b border-gray-200 px-4 py-3">
          <Search className="w-5 h-5 text-gray-400 mr-3" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                console.log('=== ENTER KEY PRESSED ===');
                console.log('Search term:', searchTerm);
                console.log('Filtered commands count:', filteredCommands.length);
                console.log('Selected index:', selectedIndex);
                console.log('All filtered commands:', filteredCommands.map(c => ({ id: c.id, desc: c.description })));
                
                if (filteredCommands.length > 0) {
                  // Prioritize add-employee command if search starts with "add employee"
                  let commandToExecute;
                  const searchLowerTrim = searchTerm.toLowerCase().trim();
                  if (searchLowerTrim.startsWith('add employee') || 
                      searchLowerTrim.startsWith('new employee') ||
                      searchLowerTrim.startsWith('create employee')) {
                    const addEmployeeCmd = filteredCommands.find(c => c.id === 'add-employee');
                    if (addEmployeeCmd) {
                      commandToExecute = addEmployeeCmd;
                      console.log('Prioritizing add-employee command based on search term');
                    } else {
                      commandToExecute = filteredCommands[selectedIndex] || filteredCommands[0];
                    }
                  } else {
                    commandToExecute = filteredCommands[selectedIndex] || filteredCommands[0];
                  }
                  
                  console.log('Command to execute:', commandToExecute);
                  
                  if (commandToExecute) {
                    console.log('Executing from input Enter:', commandToExecute.id);
                    console.log('Search term for parsing:', searchTerm);
                    executeCommand(commandToExecute);
                  } else {
                    console.error('No command to execute - commandToExecute is null/undefined');
                  }
                } else {
                  console.error('No filtered commands available - search term might not match any command');
                  console.log('Available commands:', commands.map(c => ({ id: c.id, keywords: c.keywords })));
                }
              }
            }}
            placeholder="Type a command or search..."
            className="flex-1 outline-none text-gray-900 placeholder-gray-400"
          />
          <div className="flex items-center gap-2 ml-4">
            <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">
              ESC
            </kbd>
          </div>
        </div>

        {/* Commands List */}
        <div className="max-h-96 overflow-y-auto">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <p>No commands found</p>
              <p className="text-sm mt-2">Try "open emirates" or "add employee"</p>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(groupedCommands).map(([category, cmds]) => (
                <div key={category} className="mb-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {category}
                  </div>
                  {cmds.map((cmd, index) => {
                    const globalIndex = filteredCommands.findIndex(c => c.id === cmd.id);
                    const isSelected = globalIndex === selectedIndex;
                    
                    return (
                      <button
                        key={cmd.id}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          executeCommand(cmd);
                        }}
                        className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{cmd.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {cmd.keywords[0]}
                          </div>
                        </div>
                        {isSelected && (
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-2 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
              <span>Select</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;

